import { useQuery } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import type { NotesTable } from "@msuite/utils/types";
import { client } from "@/hono";

/** Options Interface */
interface UseNotesOptions {
	/**
	 * Whether the caller may read the parent these notes hang on. False keeps the request unsent:
	 * the route answers a caller who cannot read the parent with a 403, and a hidden surface that
	 * asks anyway turns that into a toast on a page nobody clicked.
	 */
	enabled?: boolean;
}

export function useNotes(notesTable: NotesTable, identifier: string, options?: UseNotesOptions) {
	/** Query */
	const query = useQuery({
		queryKey: ["notes", notesTable, identifier],
		queryFn: async () => {
			const response = await client.notes[":notesTable"][":identifier"].$get({
				param: {
					notesTable,
					identifier,
				},
			});
			// The refusal is not news to the reader: reading a note follows the parent, and a caller
			// whose rule stops at the row rather than the category learns it here. The surface shows
			// its no-access state instead of raising a toast over the page.
			const data = await responseHandler(response, false);
			return data.notes;
		},
		enabled: options?.enabled ?? true,
	});

	return {
		...query,
		notes: query.data || [],
	};
}
